// ═══════════════════════════════════════════════════════════════════════════
// OPTIMIZED WORKSTATION EDIT DIALOG - NO TYPING DELAY
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, memo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import { CreateDialog, FieldLabel } from "./shared";

const WorkstationEditDialog = memo(function WorkstationEditDialog({
  open,
  onClose,
  onSave,
  saving,
  workstation,  // Initial workstation data
}) {
  // ✅ Local state - only this dialog re-renders on typing
  const [form, setForm] = useState({
    MachineCode: "",
    MachineGroupCode: "",
    GroupDescription: "",
    MachineDescription: "",
    HoursType: "Machine",
    FinishedGood: 0,
    active_status: 1,
  });

  // Update form when workstation changes
  useEffect(() => {
    if (workstation) {
      setForm({
        MachineCode: workstation.MachineCode || "",
        MachineGroupCode: workstation.MachineGroupCode || "",
        GroupDescription: workstation.GroupDescription || "",
        MachineDescription: workstation.MachineDescription || "",
        HoursType: workstation.HoursType || "Machine",
        FinishedGood: workstation.FinishedGood ?? 0,
        active_status: workstation.active_status ?? 1,
      });
    }
  }, [workstation]);

  const h = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    onSave(form);
  };

  return (
    <CreateDialog
      open={open}
      onClose={handleClose}
      title="Edit Workstation"
      icon={<EditIcon />}
      color="#2563eb"
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <FieldLabel>Machine Code</FieldLabel>
          <TextField
            fullWidth
            size="small"
            value={form.MachineCode}
            disabled
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FieldLabel>Machine Group</FieldLabel>
          <TextField
            fullWidth
            size="small"
            value={form.MachineGroupCode || ""}
            onChange={h("MachineGroupCode")}
            autoFocus
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FieldLabel>Group Description</FieldLabel>
          <TextField
            fullWidth
            size="small"
            value={form.GroupDescription || ""}
            onChange={h("GroupDescription")}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FieldLabel>Machine Description</FieldLabel>
          <TextField
            fullWidth
            size="small"
            value={form.MachineDescription || ""}
            onChange={h("MachineDescription")}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <FieldLabel>Hours Type</FieldLabel>
          <FormControl fullWidth size="small">
            <Select
              value={form.HoursType || "Machine"}
              onChange={h("HoursType")}
            >
              <MenuItem value="Machine">Machine Hours</MenuItem>
              <MenuItem value="Man">Man Hours</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FieldLabel>Finished Good</FieldLabel>
          <FormControl fullWidth size="small">
            <Select
              value={Number(form.FinishedGood ?? 0)}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  FinishedGood: Number(e.target.value),
                }))
              }
            >
              <MenuItem value={0}>No</MenuItem>
              <MenuItem value={1}>Yes</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FieldLabel>Active Status</FieldLabel>
          <FormControl fullWidth size="small">
            <Select
              value={Number(form.active_status ?? 1)}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  active_status: Number(e.target.value),
                }))
              }
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          sx={{ textTransform: "none" }}
          disabled={saving}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveIcon />}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Box>
    </CreateDialog>
  );
});

export default WorkstationEditDialog;
